import { ReactElement, ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { CSSTransition } from 'react-transition-group'
import closeIcon from 'assets/images/close-icon.svg'
import CloseButton from 'components/common/CloseButton'
import ReactPortal from 'components/ReactPortal'

export interface ModalProps {
    children: ReactNode
    title?: string
    isOpen: boolean
    handleClose: () => void
}

export const Modal = ({
    children,
    isOpen,
    title,
    handleClose,
}: ModalProps): ReactElement | null => {
    // 'ref' for element to be transitioned using CSSTransition
    const nodeRef = useRef(null)

    // close modal on 'Escape' key press
    useEffect(() => {
        const closeOnEscapeKey = (e: KeyboardEvent): any =>
            e.key === 'Escape' ? handleClose() : null
        document.body.addEventListener('keydown', closeOnEscapeKey)
        return () => {
            document.body.removeEventListener('keydown', closeOnEscapeKey)
        }
    }, [handleClose])

    return (
        <>
            <ReactPortal wrapperId="react-portal-modal-container">
                <CSSTransition
                    in={isOpen}
                    timeout={300}
                    unmountOnExit
                    classNames="modal-fade"
                    nodeRef={nodeRef}
                >
                    <>
                        {isOpen && (
                            <Container
                                ref={nodeRef}
                                onClick={() => handleClose()}
                            >
                                <ModalBody onClick={(e) => e.stopPropagation()}>
                                    <ModalHeader>
                                        <ModalTitle>{title}</ModalTitle>
                                        <CloseButtonContainer>
                                            <CloseButton
                                                src={closeIcon}
                                                onClick={() => handleClose()}
                                                width={24}
                                                height={24}
                                            ></CloseButton>
                                        </CloseButtonContainer>
                                    </ModalHeader>
                                    <ModalContent>{children}</ModalContent>
                                    <ModalFooter />
                                </ModalBody>
                            </Container>
                        )}
                    </>
                </CSSTransition>
            </ReactPortal>
        </>
    )
}

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0.5rem;
    opacity: 0.35;
`

const Container = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    will-change: transform, opacity;
    &.modal-fade-enter {
        opacity: 0;
        transform: scale(0.9);
    }
    &.modal-fade-enter-active {
        opacity: 1;
        transform: translateX(0);
        transition:
            opacity 300ms,
            transform 300ms;
    }
    &.modal-fade-exit {
        opacity: 1;
    }
    &.modal-fade-exit-active {
        opacity: 0;
        transform: scale(0.9);
        transition:
            opacity 300ms,
            transform 300ms;
    }

    @media screen and (max-width: 575px) {
        &.modal-fade-enter {
            opacity: 0;
            transform: translateY(100vh);
        }
        &.modal-fade-enter-active {
            opacity: 1;
            transform: translateX(0) translateY(0);
            transition:
                opacity 300ms,
                transform 300ms;
        }
        &.modal-fade-exit {
            opacity: 1;
        }
        &.modal-fade-exit-active {
            opacity: 0;
            transform: translateY(100vh);
            transition:
                opacity 300ms,
                transform 300ms;
        }
    }
`

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 80vw;
    height: 90vh;
    background-color: #fff;
    padding: 0.4em;
    box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    overflow: hidden;
    @media screen and (max-width: 575px) {
        width: 100vw;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
    }
`

const ModalHeader = styled.div`
    position: relative;
    display: flex;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
`

const ModalTitle = styled.span`
    font-size: 1.2rem;
    flex: 1;
    text-align: center;
    font-weight: 600;
`

const ModalContent = styled.div`
    flex: 1;
    border-radius: 0.25rem;
    overflow: auto;
`

const ModalFooter = styled.div``
